import {
  ConsentPurpose,
  EmailTemplate,
  Event,
  EventWithFlows,
  Flow,
  FormResponse,
} from '@dynamic-flow/types';
import { appConfig } from '../config/appConfig';

// Use configuration instead of hardcoded values
const API_BASE_URL = `${appConfig.api.baseUrl}`;
type ExportFormat = 'json' | 'csv';

export const MOCK_PURPOSES: ConsentPurpose[] = [
  {
    id: 'purpose-1',
    name: 'Dynamic Form Consent',
    purposeId: '06f3bba5-0042-45e8-8acb-c128e706bf7a',
    description: 'Consent for marketing emails and promotions',
  },
];

// Generic API client
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Try to get Firebase auth token if available
    try {
      const { auth } = await import('../config/firebaseConfig');
      const currentUser = auth.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Could not get auth token:', error);
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const authHeaders = await this.getAuthHeaders();

    const config: RequestInit = {
      headers: {
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return {} as T;
      }

      const data = await response.json();

      // Handle API response wrapper
      if (data && typeof data === 'object' && 'success' in data) {
        if (!data.success) {
          throw new Error(data.error || 'API request failed');
        }
        // Return data field if it exists, otherwise return the whole response
        return (data.data !== undefined ? data.data : data) as T;
      }

      return data;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Flow endpoints
  async getFlows(
    params: {
      status?: string;
      search?: string;
      limit?: number;
      page?: number;
    } = {}
  ): Promise<{ flows: Flow[]; pagination: any }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const endpoint = `/flows${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.request<any>(endpoint);

    // Handle both old and new response formats
    if (response.data && response.pagination) {
      return {
        flows: response.data,
        pagination: response.pagination,
      };
    } else if (Array.isArray(response)) {
      // Fallback for direct array response
      return {
        flows: response,
        pagination: { page: 1, limit: response.length, total: response.length },
      };
    } else {
      // Fallback for other response formats
      return {
        flows: response.flows || response.data || [],
        pagination: response.pagination || { page: 1, limit: 50, total: 0 },
      };
    }
  }

  async getFlow(id: string): Promise<Flow> {
    const response = await this.request<any>(`/flows/${id}`);

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async getDraftFlow(id: string, lang?: string): Promise<Flow> {
    const response = await this.request<any>(
      `/flows/${id}/draft${lang ? `?lang=${lang}` : ''}`
    );

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async getPublishedFlow(id: string): Promise<Flow> {
    const response = await this.request<any>(`/flows/${id}/published`);

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async createFlow(flow: Partial<Flow>): Promise<Flow> {
    const response = await this.request<any>('/flows', {
      method: 'POST',
      body: JSON.stringify(flow),
    });

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async updateFlow(id: string, flow: Partial<Flow>): Promise<Flow> {
    const response = await this.request<any>(`/flows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(flow),
    });

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async deleteFlow(id: string): Promise<void> {
    return this.request<void>(`/flows/${id}`, {
      method: 'DELETE',
    });
  }

  async publishFlow(id: string, flowData?: Partial<Flow>): Promise<Flow> {
    const response = await this.request<any>(`/flows/${id}/publish`, {
      method: 'POST',
      body: flowData ? JSON.stringify(flowData) : undefined,
    });

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async unpublishFlow(id: string): Promise<Flow> {
    const response = await this.request<any>(`/flows/${id}/unpublish`, {
      method: 'POST',
    });

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async duplicateFlow(id: string): Promise<Flow> {
    const response = await this.request<any>(`/flows/${id}/duplicate`, {
      method: 'POST',
    });

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async getFlowAnalytics(id: string): Promise<any> {
    const response = await this.request<any>(`/flows/${id}/analytics`);

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  // Response endpoints
  async getResponses(
    params: {
      flowId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      page?: number;
    } = {}
  ): Promise<{ responses: FormResponse[]; pagination: any }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const endpoint = `/responses${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.request<any>(endpoint);

    // Handle both old and new response formats
    if (response.data && response.pagination) {
      return {
        responses: response.data,
        pagination: response.pagination,
      };
    } else if (Array.isArray(response)) {
      // Fallback for direct array response
      return {
        responses: response,
        pagination: { page: 1, limit: response.length, total: response.length },
      };
    } else {
      // Fallback for other response formats
      return {
        responses: response.responses || response.data || [],
        pagination: response.pagination || { page: 1, limit: 50, total: 0 },
      };
    }
  }

  async getResponse(id: string): Promise<FormResponse> {
    const response = await this.request<any>(`/responses/${id}`);

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async submitResponse(
    response: FormResponse
  ): Promise<{ id: string; message: string; submittedAt: string }> {
    const apiResponse = await this.request<any>('/responses', {
      method: 'POST',
      body: JSON.stringify(response),
    });

    // Handle both old and new response formats
    if (apiResponse.data) {
      return apiResponse.data;
    }
    return apiResponse;
  }

  async exportResponses<T = unknown>(
    flowId: string,
    format: ExportFormat = 'json'
  ): Promise<T | Blob> {
    const response = await fetch(
      `${this.baseUrl}/responses/flow/${flowId}/export?format=${format}`,
      {
        headers: {
          Accept: format === 'csv' ? 'text/csv' : 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `Export failed (${response.status}): ${errorText || response.statusText}`
      );
    }

    if (format === 'csv') {
      return await response.blob();
    }

    return (await response.json()) as T;
  }

  async getResponseAnalytics(flowId: string): Promise<any> {
    const response = await this.request<any>(
      `/responses/flow/${flowId}/analytics`
    );

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  // Global analytics
  async getGlobalAnalytics(): Promise<any> {
    const response = await this.request<any>('/analytics');

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  // Health check
  async healthCheck(): Promise<{
    status: string;
    timestamp: string;
    version: string;
  }> {
    const response = await this.request<any>('/health', {
      method: 'GET',
    });

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async getEvents(
    params: {
      search?: string;
      limit?: number;
      page?: number;
    } = {}
  ): Promise<{ events: Event[]; pagination: any }> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const endpoint = `/events${searchParams.toString() ? `?${searchParams}` : ''}`;
    const response = await this.request<any>(endpoint);

    // Handle both old and new response formats
    if (response.data && response.pagination) {
      return {
        events: response.data,
        pagination: response.pagination,
      };
    } else if (Array.isArray(response)) {
      // Fallback for direct array response
      return {
        events: response,
        pagination: { page: 1, limit: response.length, total: response.length },
      };
    } else {
      // Fallback for other response formats
      return {
        events: response.responses || response.data || [],
        pagination: response.pagination || { page: 1, limit: 50, total: 0 },
      };
    }
  }

  async getEvent(id: string): Promise<EventWithFlows> {
    const response = await this.request<any>(`/events/${id}`, {
      method: 'GET',
    });

    if (response.data) {
      return response.data;
    }

    return response;
  }

  async createEvent(event: Partial<Event>): Promise<Event> {
    const response = await this.request<any>('/events', {
      method: 'POST',
      body: JSON.stringify(event),
    });

    if (response.data) {
      return response.data;
    }

    return response;
  }

  async updateEvent(id: string, event: Partial<Event>): Promise<Event> {
    const response = await this.request<any>(`/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(event),
    });

    if (response.data) {
      return response.data;
    }
    return response;
  }

  async deleteEvent(id: string): Promise<void> {
    const response = await this.request<any>(`/events/${id}`, {
      method: 'DELETE',
    });

    if (response.data) {
      return response.data;
    }
    return response;
  }

  async getPurposes(): Promise<ConsentPurpose[]> {
    // TODO: Replace with actual API call
    // const { data } = await apiClient.get('/consent/purposes', { params: filters });
    // return data;

    await new Promise((resolve) => setTimeout(resolve, 300));

    return MOCK_PURPOSES;
  }

  async saveFlowTranslation(
    id: string,
    lang: string,
    flow: Partial<Flow>
  ): Promise<Flow> {
    const response = await this.request<any>(
      `/flows/${id}/translation/${lang}`,
      {
        method: 'PUT',
        body: JSON.stringify(flow),
      }
    );

    // Handle both old and new response formats
    if (response.data) {
      return response.data;
    }
    return response;
  }

  async getEmailTemplates(): Promise<EmailTemplate[]> {
    const endpoint = '/email-templates';
    const response = await this.request<any>(endpoint);
    if (response.data) {
      return response.data ?? [];
    }
    return response ?? [];
  }
}

// Create API client instance
export const apiClient = new ApiClient(API_BASE_URL);

// Convenience functions
export const flowsApi = {
  getAll: (params?: Parameters<typeof apiClient.getFlows>[0]) =>
    apiClient.getFlows(params),
  getById: (id: string) => apiClient.getFlow(id),
  getDraft: (id: string, lang?: string) => apiClient.getDraftFlow(id, lang),
  getPublished: (id: string) => apiClient.getPublishedFlow(id),
  create: (flow: Partial<Flow>) => apiClient.createFlow(flow),
  update: (id: string, flow: Partial<Flow>) => apiClient.updateFlow(id, flow),
  delete: (id: string) => apiClient.deleteFlow(id),
  publish: (id: string, flowData?: Partial<Flow>) =>
    apiClient.publishFlow(id, flowData),
  unpublish: (id: string) => apiClient.unpublishFlow(id),
  duplicate: (id: string) => apiClient.duplicateFlow(id),
  getAnalytics: (id: string) => apiClient.getFlowAnalytics(id),
  saveTranslation: (id: string, lang: string, flow: Partial<Flow>) =>
    apiClient.saveFlowTranslation(id, lang, flow),
};

export const eventApi = {
  getAll: (params?: Parameters<typeof apiClient.getEvents>[0]) =>
    apiClient.getEvents(params),
  getById: (id: string) => apiClient.getEvent(id),
  create: (event: Partial<Event>) => apiClient.createEvent(event),
  update: (id: string, event: Partial<Event>) =>
    apiClient.updateEvent(id, event),
  delete: (id: string) => apiClient.deleteEvent(id),
};

export const emailTemplateApi = {
  getAll: () => apiClient.getEmailTemplates(),
};

export const responsesApi = {
  getAll: (params?: Parameters<typeof apiClient.getResponses>[0]) =>
    apiClient.getResponses(params),
  getById: (id: string) => apiClient.getResponse(id),
  submit: (response: FormResponse) => apiClient.submitResponse(response),
  export: <T>(flowId: string, format?: 'json' | 'csv') =>
    apiClient.exportResponses<T>(flowId, format),
  getAnalytics: (flowId: string) => apiClient.getResponseAnalytics(flowId),
};

export const analyticsApi = {
  getGlobal: () => apiClient.getGlobalAnalytics(),
};

// Convenience function for form submission
export const submitFormResponse = async (responseData: FormResponse) => {
  return responsesApi.submit(responseData);
};

export const consentApi = {
  getPurposes: () => apiClient.getPurposes(),
};

export default apiClient;
